<template>
  <PageContainer title="Faturamento AE">
    <div slot="header" class="page-buttom">
      <Button @handle:click="generateRetroactive" text="Gerar faturamento anterior" color="secondary"
        :disabled="loadingRetroactive">
        <FileArrowUpDownOutline :size="18" />
      </Button>

      <p>Necessário selecionar
        a Autoescola</p>
    </div>

    <Box class="page-container" space="medium">
      <div class="loader" v-if="!loaded">
        <LoadingSpinner />
      </div>

      <div class="page-content" v-else>
        <form @submit.prevent="handleSubmit">
          <div class="radio">
            <span>Selecione o critério de consulta</span>

            <RadioButtons :options="consultOptions" name="consult" :modelValue="consult"
              @update:modelValue="$event => this.consult = $event" />
          </div>

          <div class="inputs">
            <div class="inputs-row">
              <Select class="input-full" :options="schemaOptions" legend="Autoescola" placeholder="Selecione"
                :modelValue="schema" @update:modelValue="$event => (this.schema = $event)" />

              <template v-if="consult === 'period'">
                <Period legend="Data" :firstModelValue="initialDate" :secondModelValue="finalDate"
                  @update:firstModelValue="$event => (this.initialDate = $event)"
                  @update:secondModelValue="$event => (this.finalDate = $event)" :disabled="loading" />

                <Select class="input-full" :options="processoOptions" legend="Processo"
                  placeholder="Selecione o processo" :modelValue="processo"
                  @update:modelValue="$event => (this.processo = $event)" :disabled="loading" />

                <Select class="input-full" :options="statusOptions" legend="Status" placeholder="Selecione o status"
                  :modelValue="status" @update:modelValue="$event => (this.status = $event)" :disabled="loading" />
              </template>

              <template v-else-if="consult === 'student'">
                <Input class="input-full" legend="Aluno(a)" placeholder="Digite o nome do aluno(a)" :modelValue="aluno"
                  @update:modelValue="$event => (this.aluno = $event)" :disabled="loading" />
              </template>

              <template v-else>
                <Input class="input-full" legend="Código de barras" placeholder="Digite o código de barras"
                  :modelValue="barcode" @update:modelValue="$event => (this.barcode = $event)" :disabled="loading" />
              </template>
            </div>
          </div>

          <div class="buttons">
            <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
              :disabled="loading">
              <CloseCircleOutline :size="18" />
            </Button>

            <Button type="submit" text="Consultar" color="primary" :disabled="loading">
              <Magnify :size="18" />
            </Button>
          </div>
        </form>

        <div class="loader" v-if="loading">
          <LoadingSpinner />
        </div>

        <div v-else-if="faturas.length > 0 && !loading" class="table-container">
          <div class="infos">
            <Input legend="Pago" :readonly="true" :modelValue="`R$ ${pago}`" />
            <Input legend="Pendente" :readonly="true" :modelValue="`R$ ${pendente}`" />
            <Input legend="Gratuidade" :readonly="true" :modelValue="`R$ ${gratuidade}`" />
            <Button type="button" @handle:click="openConfirmGenerateModal" text="Gerar boleto" color="primary"
              :disabled="loading || selectedFaturas.length < 1">
              <Barcode :size="18" />
            </Button>
          </div>

          <div class="table-header">
            <StatusLegend :statusOptions="statusLegendOptions" />
          </div>

          <Table :headers="['Status', 'Candidato', 'CPF', 'Processo', 'Valor a pagar', 'Ações']"
            :selectable="true" :selected-items="selectedFaturas" :all-items="faturas" @select-all="selectAll"
            @toggle-selection="toggleSelection">
            <tr v-for="fatura in faturas" :key="fatura.id" :class="{ selected: selectedFaturas.includes(fatura.id) }">
              <td v-if="!fatura.link && fatura.status !== 'PAGO'" class="checkbox">
                <label>
                  <input type="checkbox" :checked="selectedFaturas.includes(fatura.id)"
                    @change="toggleSelection(fatura)" />
                  <span></span>
                </label>
              </td>
              <td v-else></td>
              <td>
                <div class="badges">
                  <BadgeCircle v-if="fatura.status === 'PAGO' && fatura.baixa_manual" color="blue" />
                  <BadgeCircle v-else-if="fatura.status === 'PAGO'" color="green" />
                  <BadgeCircle v-else-if="fatura.status === 'PENDENTE'" color="red" />
                  <BadgeCircle v-else-if="fatura.status === 'AGUARDANDO CONFIRMAÇÂO DE AULA'" color="yellow" />
                  <BadgeCircle v-else />
                </div>
              </td>
              <td>
                <div class="cel-m-space">
                  {{ fatura.nome }}
                </div>
              </td>
              <td>
                <div class="cel-s-space">
                  {{ utils.formatarCPF(fatura.cpf) }}
                </div>
              </td>
              <td>{{ fatura.codigo_processo }} - {{ fatura.tipo }}</td>
              <td>
                <div class="cel-s-space">
                  {{ fatura.valor }}
                </div>
              </td>
              <td>
                <div class="actions">
                  <div>
                    <IconButton @handle:click="openInfoModal(fatura)" color="secondary" title="Detalhes"
                      :disabled="loadingActions">
                      <InformationOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="fatura.link">
                    <IconButton @handle:click="ticketPDFDownlaod(fatura.link)" color="success" title="Baixar boleto"
                      :disabled="loadingActions">
                      <FileOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="fatura.link">
                    <IconButton @handle:click="getBordero(fatura.id)" color="primary" title="Visualizar Borderô"
                      :disabled="loadingActions">
                      <EyeOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="fatura.link && !fatura.pago">
                    <IconButton @handle:click="openUpdateModal(fatura.id)" color="primary" title="Atualizar boleto"
                      :disabled="loadingActions">
                      <Cached :size="19" />
                    </IconButton>
                  </div>
                  <div
                    v-if="!fatura.link && fatura.status === 'PENDENTE' && (fatura.discountValue === '0,00' || fatura.discountValue === null)">
                    <IconButton @handle:click="openDiscountModal(fatura.id)" color="secondary" title="Dar desconto"
                      :disabled="loadingActions">
                      <PercentOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="fatura.link && fatura.status !== 'PAGO'">
                    <IconButton @handle:click="openDeleteModal(fatura.id)" color="danger" title="Cancelar Boleto"
                      :disabled="loadingActions">
                      <Close :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="['PENDENTE', 'AGUARDANDO CONFIRMAÇÂO DE AULA'].includes(fatura.status) && isAuthorizedForBaixaManual">
                    <IconButton @handle:click="openBaixaManualModal(fatura.id)" color="success" title="Baixa Manual"
                      :disabled="loadingActions">
                      <CashCheck :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="fatura.status === 'PAGO' && fatura.baixa_manual">
                    <IconButton @handle:click="openVisualizarBaixaModal(fatura.id)" color="secondary" title="Visualizar baixa manual"
                      :disabled="loadingActions">
                      <EyeOutline :size="19" />
                    </IconButton>
                  </div>
                </div>
              </td>
            </tr>
          </Table>
        </div>
      </div>
    </Box>

    <ConfirmGenerateTicketModal ref="confirmGenerateTicketModal" :value="faturasSelectedValue"
      @handle:confirm="ticketGenerate" :loading="loadingConfirm" />

    <iframe id="faturamento-retroativo" src="" hidden></iframe>

    <FaturamentoInfoModal ref="infoModal" :schema="schema" />

    <ConfirmModal ref="confirmDeleteModal" title="Cancelar boleto"
      message="Tem certeza que deseja Cancelar esse boleto? está ação é irreversível e o boleto não poderá ser recuperado!"
      :onConfirm="cancelTicket" />

    <ConfirmModal ref="confirmUpdateModal" title="Atualizar boleto"
      message="Tem certeza que deseja atualizar esse boleto?" :onConfirm="updateTicket" />

    <DiscountModal ref="discountModal" @submit:sucess="fetchFaturas" />

    <BaixaManualModal ref="baixaManualModal" @submit:success="fetchFaturas" />

    <VisualizarBaixaManualModal ref="visualizarBaixaModal" :baixa="baixaSelecionada" />
  </PageContainer>
</template>

<script>
import _export from '../../../services/export'
import utils from '../../../services/utils'
import PageContainer from 'core/PageContainer'
import Button from 'core/Button'
import LoadingSpinner from 'core/LoadingSpinner'
import Box from 'core/Box'
import RadioButtons from 'core/RadioButtons'
import Period from 'core/Period'
import Select from 'core/Select'
import Input from 'core/Input'
import StatusLegend from 'core/StatusLegend'
import Table from 'core/Table'
import BadgeCircle from 'core/BadgeCircle'
import IconButton from 'core/IconButton'
import ConfirmModal from 'core/ConfirmModal'
import FileArrowUpDownOutline from 'icons/FileArrowUpDownOutline'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import InformationOutline from 'icons/InformationOutline'
import EyeOutline from 'icons/EyeOutline'
import Close from 'icons/Close'
import Barcode from 'icons/Barcode'
import FileOutline from 'icons/FileOutline'
import Cached from 'icons/Cached'
import PercentOutline from 'icons/PercentOutline'
import CashCheck from 'icons/CashCheck'
import FaturamentoInfoModal from './modal/FaturamentoInfoModal'
import ConfirmGenerateTicketModal from './modal/ConfirmGenerateTicketModal'
import DiscountModal from './modal/DiscountModal'
import BaixaManualModal from './modal/BaixaManualModal'
import VisualizarBaixaManualModal from '../baixa-manual/VisualizarBaixaManualModal'

export default {
  name: 'FaturasBoletos',
  components: {
    PageContainer,
    Button,
    LoadingSpinner,
    Box,
    RadioButtons,
    Period,
    Select,
    Input,
    StatusLegend,
    Table,
    BadgeCircle,
    IconButton,
    ConfirmModal,

    FileArrowUpDownOutline,
    CloseCircleOutline,
    Magnify,
    InformationOutline,
    EyeOutline,
    Close,
    Barcode,
    FileOutline,
    Cached,
    PercentOutline,
    CashCheck,

    ConfirmGenerateTicketModal,
    FaturamentoInfoModal,
    DiscountModal,
    BaixaManualModal,
    VisualizarBaixaManualModal,
  },

  data() {
    return {
      utils: utils,
      consult: 'period',
      schema: '',
      initialDate: '',
      finalDate: '',
      processo: '',
      status: '',
      aluno: '',
      barcode: '',
      pago: 0,
      pendente: 0,
      gratuidade: 0,
      faturasSelectedValue: 0,
      consultOptions: [
        { value: "period", label: "Período" },
        { value: "student", label: "Nome do Aluno" },
        { value: "barcode", label: "Código de barras" },
      ],
      schemaOptions: [],
      processoOptions: [
        { value: 1, text: 'Teórico' },
        { value: 2, text: 'Prático' },
        { value: 8, text: 'Prática (Moto)' },
        { value: 3, text: 'Inclusão' },
        { value: 9, text: 'Inclusão (Moto)' },
        { value: 4, text: 'Reciclagem' },
        { value: 5, text: 'Renovação' },
      ],
      statusOptions: [
        { value: 2, text: 'Pago' },
        { value: 1, text: 'Pendente' },
        { value: 4, text: 'Gratuidade' },
        { value: 7, text: 'Aguardando confirmação da aula' },
      ],
      faturas: [],
      selectedFaturas: [],
      statusLegendOptions: [
        { legend: 'Pago', color: 'green' },
        { legend: 'Pago (Baixa Manual)', color: 'blue' },
        { legend: 'Pendente', color: 'red' },
        { legend: 'Aguardando confirmação de aula', color: 'yellow' },
        { legend: 'Gratuidade' },
      ],
      loaded: false,
      loading: false,
      loadingRetroactive: false,
      loadingActions: false,
      loadingConfirm: false,
      baixaSelecionada: null,
    }
  },
  computed: {
    isAuthorizedForBaixaManual() {
      return localStorage.email === 'nadlo.cavalcante_financeiro@fabricainfo.com' || localStorage.email === 'demys@fabricainfo.com'
    }
  },
  async created() {
    await this.fetchCfcs()

    this.loaded = true
  },
  methods: {
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados.map(cfc => {
            const sindicatoSuffix = cfc.sindicato ? ' - Sindicato' : ''
            return {
              value: cfc.tenant_schema,
              text: `${cfc.nome_fantasia}${sindicatoSuffix}`,
            }
          })
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error, {
            position: 'top-right',
          })
        })
    },
    clearInputs() {
      this.consult = 'period'
      this.schema = ''
      this.initialDate = ''
      this.finalDate = ''
      this.processo = ''
      this.status = ''
      this.aluno = ''
      this.barcode = ''
    },
    async handleSubmit() {
      if (this.validateInputs()) {
        await this.fetchFaturas()
      }
    },
    validateInputs() {
      if (!this.schema) {
        this.$toasted.show('Selecione uma autoescola!', {
          position: 'top-right',
        })

        return false
      }

      switch (this.consult) {
        case 'period':
          if (!this.initialDate && !this.finalDate) {
            this.$toasted.show('Preencha o todas as datas.', {
              position: 'top-right',
            })
            return false
          }

          if (this.initialDate && this.finalDate) {
            const initial = new Date(this.initialDate)
            const final = new Date(this.finalDate)

            if (initial > final) {
              this.$toasted.show('A data inicial deve ser menor ou igual à data final.', {
                position: 'top-right',
              })
              return false
            }
          }

          break
        case 'student':
          if (!this.aluno) {
            this.$toasted.show('Preencha o campo "Aluno".', {
              position: 'top-right',
            })
            return false
          }

          break
        case 'barcode':
          if (!this.barcode) {
            this.$toasted.show('Preencha o campo "Código de barras".', {
              position: 'top-right',
            })
            return false
          }

          break
      }

      return true
    },
    async fetchFaturas() {
      this.loading = true

      const params = {
        schema: this.schema,
      }

      switch (this.consult) {
        case 'period':
          params.periodo = [this.initialDate, this.finalDate]
          params.tipo = this.processo
          params.status = this.status

          break
        case 'student':
          params.nome = this.aluno

          break
        case 'barcode':
          params.codigoBarras = this.barcode

          break
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/faturamento/consulta`, {
        params,
      })
        .then(response => {
          if (response.body.dados.length < 1) {
            this.$toasted.show('Sem resultados para a sua pesquisa.', {
              position: 'top-right',
            })
          }

          this.selectedFaturas = []
          this.pago = response.body.pago
          this.pendente = response.body.pendente
          this.gratuidade = response.body.gratuidade
          this.faturas = response.body.dados
        })
        .catch(err => {
          this.$toasted.show(`Erro ao buscar faturas: ${err.body.mensagem}`, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectAll(isChecked) {
      if (isChecked) {
        this.selectedFaturas = this.faturas
          .filter(fatura => !fatura.link && fatura.status !== 'PAGO')
          .map((fatura) => fatura.id)
      } else {
        this.selectedFaturas = []
      }
    },
    toggleSelection(fatura) {
      const index = this.selectedFaturas.indexOf(fatura.id)
      if (index === -1) {
        this.selectedFaturas.push(fatura.id)
      } else {
        this.selectedFaturas.splice(index, 1)
      }
    },
    async generateRetroactive() {
      if (!this.schema) {
        this.$toasted.show('Necessário selecionar uma autoescola!', {
          position: 'top-right',
        })

        return
      }

      this.loadingRetroactive = true

      setTimeout(() => {
        const iframe = document.getElementById('faturamento-retroativo')

        if (iframe) {
          iframe.src = `${process.env.VUE_APP_URL_API}/faturamento/planilha?autoescola=${this.schema}`
        }

        this.loadingRetroactive = false
      }, 3000)
    },
    openConfirmGenerateModal() {
      const selectedFaturas = this.faturas.filter(fatura => this.selectedFaturas.includes(fatura.id))

      const ticketValue = selectedFaturas.reduce((total, fatura) => {
        const valorNumerico = parseFloat(fatura.valor.replace(",", "."))

        return total + valorNumerico
      }, 0)

      this.faturasSelectedValue = ticketValue

      this.$refs.confirmGenerateTicketModal.open()
    },
    async ticketGenerate() {
      this.loadingConfirm = true

      const data = {
        schema: this.schema,
        id: this.selectedFaturas,
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/boleto/criar`, data)
        .then(async response => {
          _export.downloadPDF(response.body.dataPDF, 'relacao-de-alunos')

          this.$refs.confirmGenerateTicketModal.close()

          await this.fetchFaturas()

          this.selectedFaturas = []
          this.faturasSelectedValue = 0
        })
        .catch(err => {
          this.toast('Erro ao gerar boleto.')
          this.toast(err.body.mensagem)
        })
        .finally(() => {
          this.loadingConfirm = false
          this.$refs.confirmModal.close()
        })
    },
    openInfoModal(fatura) {
      this.$refs.infoModal.open(fatura)
    },
    async ticketPDFDownlaod(link) {
      console.log(link)
      this.loadingActions = true

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/boleto/show/${this.schema}/${link}`, {
        responseType: 'blob',
      })
        .then(response => response.blob())
        .then(response => {
          _export.downloadPDFBlob(response, 'boleto')
        })
        .catch(err => {
          this.toast('Erro ao tentar baixar o boleto.')
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async getBordero(id) {
      this.loadingActions = true

      const data = { id, schema: this.schema }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/boleto/bordero`, data)
        .then(response => {
          _export.downloadPDF(response.body.dataPDF, 'bordero')
        })
        .catch(err => {
          this.toast('Erro ao gerar borderô.')
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    openDeleteModal(id) {
      this.$refs.confirmDeleteModal.open(id)
    },
    async cancelTicket(id) {
      this.loadingActions = true

      const data = {
        schema: this.schema,
        id,
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/boleto/cancelar`, data)
        .then(response => {
          this.$toasted.show(response.body.msg, {
            position: 'top-right',
          });

          this.fetchFaturas();
        })
        .catch(err => {
          if (err.body.msg) {
            this.$toasted.show(err.body.msg, {
              position: 'top-right',
            })
          } else {
            this.$toasted.show(`Erro ao cancelar o boleto.`, {
              position: 'top-right',
            })
          }
        })
        .finally(() => {
          this.loadingActions = false
          this.$refs.confirmDeleteModal.close()
        })
    },
    openUpdateModal(id) {
      this.$refs.confirmUpdateModal.open(id)
    },
    async updateTicket(id) {
      this.loadingActions = true

      const data = {
        schema: this.schema,
        id,
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/boleto/atualizar`, data)
        .then(response => {
          this.$toasted.show(response.body.mensagem, {
            position: 'top-right',
          });

          this.fetchFaturas();
        })
        .catch(err => {
          this.$toasted.show(`Erro ao atualizar o boleto: ${err.body.mensagem || ''}`, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingActions = false
          this.$refs.confirmUpdateModal.close()
        })
    },
    openDiscountModal(id) {
      this.$refs.discountModal.open(id, this.schema)
    },
    openBaixaManualModal(id) {
      this.$refs.baixaManualModal.open(id, this.schema)
    },
    async openVisualizarBaixaModal(id) {
      this.loadingActions = true
      
      try {
        const res = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/faturamento/consultar/baixa-manual`, {
          params: { 
            schema: this.schema,
            fatura_id: id
          }
        })
        
        // Buscar na lista de dados retornados pela API
        const baixaData = (res.body.data || []).find(item => item.fatura_id === id)
        
        if (baixaData) {
          this.baixaSelecionada = {
            id: baixaData.id,
            fatura_id: baixaData.fatura_id,
            autoescola: baixaData.schema,
            aluno: baixaData.aluno,
            data_baixa: baixaData.data_operacao,
            usuario: baixaData.usuario_email,
            motivo: baixaData.motivo,
            boleto_original_base64: baixaData.arquivo_boleto,
            comprovante_pagamento_base64: baixaData.arquivo_comprovante,
          }
          this.$refs.visualizarBaixaModal.open()
        } else {
          this.toast('Dados da baixa manual não encontrados.')
        }
      } catch (err) {
        this.toast('Erro ao carregar dados da baixa manual.')
      } finally {
        this.loadingActions = false
      }
    },
    toast(text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.page-buttom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  p {
    max-width: 7.5rem;
    margin: 0;
    font-size: 0.75rem;
    font-weight: 400;
    color: #606264;
  }
}

.page-container {
  .loader {
    display: flex;
    justify-content: center;
  }

  .page-content {
    form {
      display: flex;
      flex-direction: column;
      gap: 2.5rem;

      .radio {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        span {
          font-size: 1rem;
          font-weight: 600;
          line-height: 21.82px;
          color: #606264;
        }
      }

      .inputs {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;

        .inputs-row {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;

          .input-full {
            flex: 1;
          }
        }
      }

      .buttons {
        align-self: flex-end;

        display: flex;
        gap: 0.5rem;
      }
    }

    .table-container {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .infos {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 1.5rem;
      }

      .table-header {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;

        .table-legend {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.625rem;

          >div {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
        }
      }

      .checkbox {
        label {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          line-height: 19.1px;
          color: #606264;
          cursor: pointer;

          input[type="checkbox"] {
            display: none;
          }

          span {
            position: relative;
            display: inline-block;
            width: 1rem;
            height: 1rem;
            border: 2px solid #DFDCDC;
            border-radius: 2px;
            background-color: #FAFAFA;
            transition: background-color 0.2s, border-color 0.2s;
          }

          input[type="checkbox"]:checked+span {
            background-color: #1C2D5A;
          }

          input[type="checkbox"]:checked+span::after {
            content: '✓';
            color: white;
            font-size: 8px;
            position: absolute;
            top: 55%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }
      }

      .badges {
        max-width: 1rem;
      }

      .cel-s-space {
        min-width: 8rem;
      }

      .cel-m-space {
        min-width: 16rem;
      }

      .actions {
        min-width: 8rem;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        row-gap: 0.5rem;

        div {
          padding: 0 0.5rem;
          display: flex;
          justify-content: center;

          &:not(:last-child) {
            border-right: 1px solid #ECECEC;
          }
        }
      }
    }
  }
}
</style>
